import { User, Users, Settings, Video, MessageCircle } from 'lucide-react';

export interface Session {
  id: string;
  title: string;
  description: string;
  type: 'one_on_one' | 'group' | 'workshop' | 'webinar' | 'consultation';
  status: 'scheduled' | 'live' | 'completed' | 'cancelled';
  scheduledAt: string;
  duration: number; // minutes
  maxParticipants: number;
  currentParticipants: number;
  price: number;
  currency: string;
  hostId: string;
  hostName: string;
  hostAvatar: string;
  participants: Participant[];
  tags: string[];
  category: string;
  recordingUrl?: string;
  chatHistory: ChatMessage[];
  analytics: SessionAnalytics;
}

export interface Participant {
  id: string;
  name: string;
  avatar: string;
  role: 'host' | 'participant' | 'moderator';
  status: 'online' | 'offline' | 'away';
  joinedAt: string;
  leftAt?: string;
  isMuted: boolean;
  isVideoOff: boolean;
  isScreenSharing: boolean;
  connectionQuality: 'excellent' | 'good' | 'poor' | 'disconnected';
}

export interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  message: string;
  timestamp: string;
  type: 'text' | 'file' | 'image' | 'system';
  isPrivate: boolean;
}

export interface SessionAnalytics {
  totalViews: number;
  peakViewers: number;
  averageWatchTime: number;
  engagementRate: number;
  chatMessages: number;
  reactions: number;
  revenue: number;
  recordingViews: number;
  participantSatisfaction: number;
}

export const exampleLiveSessions: Session[] = [
  {
    id: '1',
    title: 'Diabetes Management Workshop',
    description: 'Interactive workshop covering diabetes management strategies, nutrition, and lifestyle changes.',
    type: 'workshop',
    status: 'scheduled',
    scheduledAt: '2025-01-25T14:00:00Z',
    duration: 90,
    maxParticipants: 50,
    currentParticipants: 23,
    price: 29.99,
    currency: 'USD',
    hostId: 'guru-1',
    hostName: 'Dr. Sarah Johnson',
    hostAvatar: '/avatars/sarah.jpg',
    participants: [
      {
        id: 'p1',
        name: 'Dr. Sarah Johnson',
        avatar: '/avatars/sarah.jpg',
        role: 'host',
        status: 'online',
        joinedAt: '2025-01-25T13:55:00Z',
        isMuted: false,
        isVideoOff: false,
        isScreenSharing: false,
        connectionQuality: 'excellent'
      },
      {
        id: 'p2',
        name: 'John Smith',
        avatar: '/avatars/john.jpg',
        role: 'participant',
        status: 'online',
        joinedAt: '2025-01-25T13:58:00Z',
        isMuted: true,
        isVideoOff: true,
        isScreenSharing: false,
        connectionQuality: 'good'
      }
    ],
    tags: ['diabetes', 'nutrition', 'workshop'],
    category: 'Chronic Conditions',
    chatHistory: [
      {
        id: 'msg1',
        senderId: 'p1',
        senderName: 'Dr. Sarah Johnson',
        message: 'Welcome everyone! We\'ll be starting in 2 minutes.',
        timestamp: '2025-01-25T13:58:00Z',
        type: 'text',
        isPrivate: false
      }
    ],
    analytics: {
      totalViews: 0,
      peakViewers: 0,
      averageWatchTime: 0,
      engagementRate: 0,
      chatMessages: 1,
      reactions: 0,
      revenue: 0,
      recordingViews: 0,
      participantSatisfaction: 0
    }
  },
  {
    id: '2',
    title: 'Stress Management Consultation',
    description: 'One-on-one consultation for stress management techniques and mental wellness.',
    type: 'one_on_one',
    status: 'live',
    scheduledAt: '2025-01-25T10:00:00Z',
    duration: 60,
    maxParticipants: 2,
    currentParticipants: 2,
    price: 75.00,
    currency: 'USD',
    hostId: 'guru-1',
    hostName: 'Dr. Sarah Johnson',
    hostAvatar: '/avatars/sarah.jpg',
    participants: [
      {
        id: 'p1',
        name: 'Dr. Sarah Johnson',
        avatar: '/avatars/sarah.jpg',
        role: 'host',
        status: 'online',
        joinedAt: '2025-01-25T10:00:00Z',
        isMuted: false,
        isVideoOff: false,
        isScreenSharing: false,
        connectionQuality: 'excellent'
      },
      {
        id: 'p3',
        name: 'Alice Brown',
        avatar: '/avatars/alice.jpg',
        role: 'participant',
        status: 'online',
        joinedAt: '2025-01-25T10:02:00Z',
        isMuted: false,
        isVideoOff: false,
        isScreenSharing: false,
        connectionQuality: 'good'
      }
    ],
    tags: ['stress', 'mental-health', 'consultation'],
    category: 'Mental Health',
    chatHistory: [
      {
        id: 'msg2',
        senderId: 'p1',
        senderName: 'Dr. Sarah Johnson',
        message: 'How have you been feeling this week?',
        timestamp: '2025-01-25T10:05:00Z',
        type: 'text',
        isPrivate: false
      }
    ],
    analytics: {
      totalViews: 2,
      peakViewers: 2,
      averageWatchTime: 25,
      engagementRate: 85,
      chatMessages: 5,
      reactions: 3,
      revenue: 75.00,
      recordingViews: 0,
      participantSatisfaction: 4.8
    }
  }
];

export const sessionTypes = [
  { id: 'one_on_one', label: 'One-on-One', icon: User, description: 'Private consultation' },
  { id: 'group', label: 'Group Session', icon: Users, description: 'Small group discussion' },
  { id: 'workshop', label: 'Workshop', icon: Settings, description: 'Interactive workshop' },
  { id: 'webinar', label: 'Webinar', icon: Video, description: 'Large audience presentation' },
  { id: 'consultation', label: 'Consultation', icon: MessageCircle, description: 'Expert consultation' }
]; 